import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion'
import { ARCS, CIRCUIT_LINES } from './geometry'

const DESIGN_SIZE = 1000
const DEFAULT_LOGO_COLOR = { r: 247, g: 177, b: 0 }
const DEFAULT_CIRCUIT_COLOR = { r: 255, g: 255, b: 255 }
const CHIP_ALPHA = 0.3

// Idle: the arcs spin. Pulse: they keep spinning and briefly get thicker.
// Move: they turn into their final position. Circuit: the circuit lines are
// drawn around them. Done: nothing moves anymore and no frames are requested.
const PHASE_IDLE = 0
const PHASE_PULSE = 1
const PHASE_MOVE = 2
const PHASE_CIRCUIT = 3
const PHASE_DONE = 4

const PULSE_DURATION = 500
const MOVE_DURATION = 2000
const CIRCUIT_DURATION = 1500

const toRgba = ({ r, g, b }, alpha) => `rgba(${r}, ${g}, ${b}, ${alpha})`

const createState = () => ({
  arcs: ARCS.map((arc) => ({
    ...arc,
    rotationSpeed: Math.random() * 0.04 - 0.02,
    pulseLineWidth: null,
  })),
  phase: PHASE_IDLE,
  // Set from the first frame after a phase starts, so a phase that starts
  // while the tab is hidden still runs completely once it is visible.
  phaseStart: null,
  circuitProgress: 0,
})

const finishImmediately = (state) => {
  state.arcs.forEach((arc) => {
    arc.angle = arc.targetAngle
    arc.radius = arc.targetRadius
    arc.pulseLineWidth = null
  })
  state.phase = PHASE_DONE
  state.phaseStart = null
  state.circuitProgress = 1
}

const drawRotatedArc = (ctx, x, y, radius, lineWidth, color, angle) => {
  ctx.beginPath()
  ctx.arc(x, y, radius, Math.PI + angle, 2 * Math.PI + angle)
  ctx.lineWidth = lineWidth
  ctx.strokeStyle = color
  ctx.shadowBlur = 30
  ctx.shadowColor = color
  ctx.stroke()
}

const drawEndpoint = (ctx, x, y, radius, color, lineScale) => {
  ctx.beginPath()
  ctx.arc(x, y, radius, 0, 2 * Math.PI)
  ctx.fillStyle = color
  ctx.shadowBlur = 20 * lineScale
  ctx.shadowColor = color
  ctx.fill()
}

/**
 * Draws the circuit lines up to `progress` (0 to 1) of their length.
 */
const drawCircuitLines = (ctx, progress, colors, scaleX, scaleY, lineScale) => {
  CIRCUIT_LINES.forEach((line) => {
    const color = line.chip ? colors.chip : colors.circuit
    ctx.beginPath()
    ctx.lineWidth = line.width * lineScale
    ctx.strokeStyle = color
    ctx.shadowBlur = 20 * lineScale
    ctx.shadowColor = color

    let x = line.x * scaleX
    let y = line.y * scaleY
    ctx.moveTo(x, y)

    const totalLength = line.segments.reduce((sum, [length]) => sum + length, 0)
    let visibleLength = progress * totalLength
    for (const [length, angleDeg] of line.segments) {
      if (visibleLength <= 0) break
      const drawLength = Math.min(length, visibleLength)
      const angle = (angleDeg * Math.PI) / 180
      x += Math.cos(angle) * drawLength * scaleX
      y += Math.sin(angle) * drawLength * scaleY
      ctx.lineTo(x, y)
      visibleLength -= drawLength
    }
    ctx.stroke()

    const pointRadius = 6 * Math.min(scaleX, scaleY)
    if (line.startPoint) drawEndpoint(ctx, line.x * scaleX, line.y * scaleY, pointRadius, color, lineScale)
    if (line.endPoint) drawEndpoint(ctx, x, y, pointRadius, color, lineScale)
  })
}

/**
 * Advances the animation to `timestamp` and draws one frame.
 *
 * @returns {boolean} Whether another frame is needed.
 */
const renderFrame = (canvas, state, colors, timestamp) => {
  const ctx = canvas.getContext('2d')
  if (!ctx) return false

  // Match the backing store to the displayed size times the pixel ratio, so
  // the drawing stays sharp on high-density screens and after resizing.
  const rect = canvas.getBoundingClientRect()
  const dpr = window.devicePixelRatio || 1
  const backingWidth = Math.floor(rect.width * dpr)
  const backingHeight = Math.floor(rect.height * dpr)
  if (canvas.width !== backingWidth || canvas.height !== backingHeight) {
    canvas.width = backingWidth
    canvas.height = backingHeight
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, rect.width, rect.height)

  if (state.phaseStart === null && state.phase !== PHASE_IDLE && state.phase !== PHASE_DONE) {
    state.phaseStart = timestamp
  }

  const centerX = rect.width / 2
  const centerY = rect.height / 2
  const arcScale = Math.min(rect.width, rect.height) / DESIGN_SIZE

  state.arcs.forEach((arc) => {
    if (state.phase === PHASE_IDLE) {
      arc.angle += arc.rotationSpeed
    } else if (state.phase === PHASE_PULSE) {
      arc.angle += arc.rotationSpeed
      const t = Math.min((timestamp - state.phaseStart) / PULSE_DURATION, 1)
      arc.pulseLineWidth = arc.lineWidth * (1 + 0.3 * Math.sin(t * Math.PI))
    } else if (state.phase === PHASE_MOVE) {
      arc.angle += (arc.targetAngle - arc.angle) * 0.08
      arc.radius += (arc.targetRadius - arc.radius) * 0.08
    }

    drawRotatedArc(
      ctx,
      centerX,
      centerY,
      arc.radius * arcScale,
      (arc.pulseLineWidth ? arc.pulseLineWidth : arc.lineWidth) * arcScale,
      toRgba(colors.logo, arc.alpha),
      arc.angle
    )
  })

  if (state.phase === PHASE_PULSE && timestamp - state.phaseStart > PULSE_DURATION) {
    state.phase = PHASE_MOVE
    state.phaseStart = timestamp
  } else if (state.phase === PHASE_MOVE && timestamp - state.phaseStart > MOVE_DURATION) {
    state.phase = PHASE_CIRCUIT
    state.phaseStart = timestamp
  }

  if (state.phase === PHASE_CIRCUIT) {
    state.circuitProgress = Math.min((timestamp - state.phaseStart) / CIRCUIT_DURATION, 1)
  }
  if (state.phase === PHASE_CIRCUIT || state.phase === PHASE_DONE) {
    drawCircuitLines(
      ctx,
      state.circuitProgress,
      colors,
      rect.width / DESIGN_SIZE,
      rect.height / DESIGN_SIZE,
      Math.min(rect.width, rect.height) / DESIGN_SIZE
    )
  }

  if (state.phase === PHASE_CIRCUIT && state.circuitProgress === 1) {
    state.phase = PHASE_DONE
    state.phaseStart = null
  }
  return state.phase !== PHASE_DONE
}

/**
 * Canvas logo of spinning arcs that assemble into the logo, after which
 * circuit lines are drawn around it. The parent sets the size through
 * `className` or `style`; other props such as `aria-hidden` are passed to the
 * canvas.
 *
 * The ref exposes `assemble()`, which starts the assemble animation. Frames
 * are only requested while something moves and while the tab is visible.
 * When the user prefers reduced motion, the assembled logo is drawn once.
 *
 * @param {object} props
 * @param {number} [props.autoAssembleDelay] - Starts assembling automatically after this many milliseconds.
 * @param {Function} [props.onCircuitStart] - Called when the circuit lines start to be drawn.
 * @param {Function} [props.onAssembled] - Called when the assemble animation has finished.
 * @param {{r: number, g: number, b: number}} [props.logoColor] - Color of the arcs.
 * @param {{r: number, g: number, b: number}} [props.circuitColor] - Color of the circuit lines.
 * @param {string} [props.className] - Class names for the canvas.
 * @param {object} [props.style] - Inline styles for the canvas.
 */
const AnimatedLogo = forwardRef(function AnimatedLogo(
  {
    autoAssembleDelay,
    onCircuitStart,
    onAssembled,
    logoColor = DEFAULT_LOGO_COLOR,
    circuitColor = DEFAULT_CIRCUIT_COLOR,
    className,
    style,
    ...canvasProps
  },
  ref
) {
  const canvasRef = useRef(null)
  const stateRef = useRef(null)
  if (stateRef.current === null) {
    stateRef.current = createState()
  }
  const frameRef = useRef(null)
  const scheduleRef = useRef(null)
  const onCircuitStartRef = useRef(onCircuitStart)
  const onAssembledRef = useRef(onAssembled)
  const reducedMotion = usePrefersReducedMotion()
  const reducedMotionRef = useRef(reducedMotion)
  const colorsRef = useRef(null)

  useEffect(() => {
    onCircuitStartRef.current = onCircuitStart
  }, [onCircuitStart])

  useEffect(() => {
    onAssembledRef.current = onAssembled
  }, [onAssembled])

  useEffect(() => {
    colorsRef.current = {
      logo: logoColor,
      circuit: toRgba(circuitColor, 1),
      chip: toRgba(circuitColor, CHIP_ALPHA),
    }
    scheduleRef.current?.()
  }, [logoColor, circuitColor])

  const assemble = useCallback(() => {
    // With reduced motion the logo is already shown assembled.
    if (reducedMotionRef.current) return
    const state = stateRef.current
    state.phase = PHASE_PULSE
    state.phaseStart = null
    scheduleRef.current?.()
  }, [])

  useImperativeHandle(ref, () => ({ assemble }), [assemble])

  useEffect(() => {
    if (autoAssembleDelay == null) return undefined
    const timeout = setTimeout(assemble, autoAssembleDelay)
    return () => clearTimeout(timeout)
  }, [autoAssembleDelay, assemble])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return undefined

    const state = stateRef.current
    reducedMotionRef.current = reducedMotion
    let hiddenAt = null

    const tick = (timestamp) => {
      frameRef.current = null
      const previousPhase = state.phase
      const needsNextFrame = renderFrame(canvas, state, colorsRef.current, timestamp)
      if (previousPhase !== PHASE_CIRCUIT && state.phase === PHASE_CIRCUIT) {
        onCircuitStartRef.current?.()
      }
      if (previousPhase !== PHASE_DONE && state.phase === PHASE_DONE) {
        onAssembledRef.current?.()
      }
      if (needsNextFrame) schedule()
    }

    function schedule() {
      if (frameRef.current === null && !document.hidden) {
        frameRef.current = requestAnimationFrame(tick)
      }
    }

    const cancel = () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        cancel()
        hiddenAt = performance.now()
        return
      }
      // Continue the running phase where it stopped instead of skipping
      // ahead by the time the tab was hidden.
      if (hiddenAt !== null && state.phaseStart !== null) {
        state.phaseStart += performance.now() - hiddenAt
      }
      hiddenAt = null
      schedule()
    }

    scheduleRef.current = schedule
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Redraws once after a resize when no frames are running.
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => schedule()) : null
    resizeObserver?.observe(canvas)

    if (reducedMotion && state.phase !== PHASE_DONE) {
      finishImmediately(state)
      onCircuitStartRef.current?.()
      onAssembledRef.current?.()
    }
    schedule()

    return () => {
      cancel()
      scheduleRef.current = null
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      resizeObserver?.disconnect()
    }
  }, [reducedMotion])

  return (
    <canvas
      ref={canvasRef}
      width={DESIGN_SIZE}
      height={DESIGN_SIZE}
      className={className}
      style={style}
      {...canvasProps}
    />
  )
})

export default AnimatedLogo
